/**
 * Typed client for the SaaS spend admin endpoints (A7Y-1495).
 * Backed by /admin/saas-spend/* — see a7y-api/src/routes/admin/saas-spend.ts.
 *
 * The endpoints don't have any shared @a7y/types definitions yet because the
 * shapes are admin-internal and unlikely to be reused. If we ever expose
 * these in the figma plugin or public webapp, lift the interfaces into
 * `packages/types/src/saasSpend.ts`.
 */
import { setTokenProvider as _setTokenProvider, type TokenProvider } from "./api";

// Re-export for SaaS spend page consumers if they want to wire auth directly,
// though the existing AdminAuthContext already does this for every page.
export const setTokenProvider = _setTokenProvider;

const baseUrl = import.meta.env.DEV
  ? "/api/admin/saas-spend"
  : `${import.meta.env.VITE_API_URL}/admin/saas-spend`;

let getTokenFn: TokenProvider | null = null;

export function setSaasSpendTokenProvider(fn: TokenProvider) {
  getTokenFn = fn;
}

async function get<T>(path: string, query?: Record<string, string | undefined>): Promise<T> {
  const url = new URL(`${baseUrl}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v != null && v !== "") url.searchParams.set(k, v);
    }
  }
  const send = async (token: string | null) => {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url.toString(), { headers });
  };

  // Same one-shot stale-token recovery as `authFetch` in ./api — see the
  // comment there for why a cached Clerk JWT outlives its expiry after the
  // tab has been backgrounded.
  const token = getTokenFn ? await getTokenFn() : null;
  let res = await send(token);
  if (res.status === 401 && token && getTokenFn) {
    const refreshed = await getTokenFn({ skipCache: true });
    if (refreshed && refreshed !== token) res = await send(refreshed);
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`SaaS spend API ${path} failed: HTTP ${res.status} ${body.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: T };
  return json.data;
}

// -------- Response types (mirrors of backend shapes) --------

export interface HeadlineMetrics {
  mtdSpend: number;
  mtdComparisonSpend: number;
  mtdDeltaPct: number;
  last30dSpend: number;
  prev30dSpend: number;
  last30dDelta7dPct: number;
  dailyBurnRate: number;
  burnSparkline60d: Array<{ date: string; amount: number }>;
}

export interface CategorySpendCell {
  month: string;
  category: string;
  amount: number;
}

export interface SpendByCategoryResponse {
  cells: CategorySpendCell[];
  categories: string[];
  months: string[];
}

export interface VendorRow {
  vendor: string;
  mtdSpend: number;
  delta30dPct: number;
  deltaYoYPct: number;
  trend: "up" | "down" | "flat";
}

export interface Anomaly {
  kind: "spike" | "missed" | "new";
  vendor: string;
  message: string;
  evidence: Record<string, unknown>;
}

export interface TransactionRow {
  date: string;
  vendor: string;
  amount: number;
  category: string;
  type: string;
  description: string;
  transactionId: string;
  rowNumber: number;
  deepLink: string;
  /** Drive URL of the archived invoice PDF, or '' when none is filed (A7Y-5015). */
  invoiceUrl?: string;
}

export interface PipelineHealth {
  lastIngestDate: string | null;
  daysSinceLastIngest: number | null;
  status: "green" | "yellow" | "red";
  totalRows: number;
  cacheFetchedAt: number | null;
  cacheExpiresAt: number | null;
}

export interface TransactionsQuery {
  from?: string;
  to?: string;
  vendor?: string;
  category?: string;
  type?: string;
  minAmount?: number | string;
  maxAmount?: number | string;
  search?: string;
}

export const saasSpendApi = {
  getHeadline: () => get<HeadlineMetrics>("/headline"),
  getByCategory: () => get<SpendByCategoryResponse>("/by-category"),
  getTopVendors: (limit?: number) =>
    get<VendorRow[]>("/top-vendors", limit != null ? { limit: String(limit) } : undefined),
  getAnomalies: () => get<Anomaly[]>("/anomalies"),
  getTransactions: (q: TransactionsQuery = {}) => {
    const query: Record<string, string | undefined> = {
      from: q.from,
      to: q.to,
      vendor: q.vendor,
      category: q.category,
      type: q.type,
      minAmount: q.minAmount != null ? String(q.minAmount) : undefined,
      maxAmount: q.maxAmount != null ? String(q.maxAmount) : undefined,
      search: q.search,
    };
    return get<TransactionRow[]>("/transactions", query);
  },
  getPipelineHealth: () => get<PipelineHealth>("/pipeline-health"),
};

/** Sheet URL for the ledger header — used as the "Edit in sheet →" affordance. */
export const LEDGER_SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1KYLYAxRRVdN8hO4DdJBelJrUrBhpRc2GUs63Ch7vmEw/edit";
